class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.load-more__button');
    this.spinner = this.querySelector('.loading__spinner');
    this.countText = this.querySelector('.load-more__count');
    this.currentPage = 1;
    this.isLoading = false;
    
    // Determine if we should use infinite scroll (mobile/tablet) or button (desktop)
    this.useInfiniteScroll = window.innerWidth < 990;
    
    this.init();
  }

  init() {
    // Hide last N products if not on last page
    this.hideLastProducts();
    
    if (this.useInfiniteScroll) {
      this.initInfiniteScroll();
    } else {
      this.initButtonClick();
    }
    
    // Re-initialize on resize with debounce
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const shouldUseInfiniteScroll = window.innerWidth < 990;
        if (shouldUseInfiniteScroll !== this.useInfiniteScroll) {
          this.useInfiniteScroll = shouldUseInfiniteScroll;
          this.observer?.disconnect();
          this.init();
        }
      }, 250);
    });
  }
  
  hideLastProducts() {
    if (!this.button) return;
    
    const merchandiseSlots = this.getMerchandiseSlots();
    if (merchandiseSlots === 0) return;
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const products = Array.from(productGrid.querySelectorAll(':scope > li:not(.merchandise-item)'));
    const productsToHide = products.slice(-merchandiseSlots);
    
    this.hideProducts(productsToHide);
  }
  
  hideProducts(products) {
    products.forEach(product => {
      product.classList.add('load-more-hidden');
      // Remove animation classes so they appear instantly when revealed
      product.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    });
  }
  
  revealHiddenProducts() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const hiddenProducts = productGrid.querySelectorAll('.load-more-hidden');
    hiddenProducts.forEach(product => product.classList.remove('load-more-hidden'));
  }

  initButtonClick() {
    if (!this.button) return;
    this.button.addEventListener('click', this.loadMore.bind(this));
  }

  initInfiniteScroll() {
    if (!this.button) return;
    
    // Use IntersectionObserver to detect when button is visible
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading) {
            this.loadMore();
          }
        });
      },
      {
        rootMargin: '200px', // Trigger 200px before button is visible
      }
    );
    
    this.observer.observe(this.button);
  }

  async loadMore() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.button?.classList.add('loading');
    this.spinner?.classList.remove('hidden');
    
    const nextPage = this.currentPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    
    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      
      this.renderProducts(html);
      this.currentPage = nextPage;
      
      // Check if there are more pages
      const hasMorePages = this.checkForMorePages(html);
      if (!hasMorePages) {
        this.button?.classList.add('hidden');
        this.observer?.disconnect();
        // Reveal all remaining hidden products on the last page
        this.revealHiddenProducts();
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.isLoading = false;
      this.button?.classList.remove('loading');
      this.spinner?.classList.add('hidden');
    }
  }

  renderProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProducts = doc.querySelectorAll('#product-grid > li');
    const productGrid = document.getElementById('product-grid');
    
    if (!productGrid || !newProducts.length) return;
    
    // Reveal previously hidden products
    this.revealHiddenProducts();
    
    // Append new products
    newProducts.forEach(product => productGrid.appendChild(product));
    
    // Hide last N products if there are more pages
    if (!this.button.classList.contains('hidden')) {
      this.hideLastProducts();
    }
    
    this.updateCount();
    
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  updateCount() {
    if (!this.countText) return;
    
    const visibleProducts = document.querySelectorAll('#product-grid > li:not(.merchandise-item)');
    // Get total from data attribute (language-independent)
    const totalProducts = parseInt(this.countText.dataset.total, 10) || visibleProducts.length;
    
    // Subtract merchandise slots if not on last page
    let currentEnd = visibleProducts.length;
    if (visibleProducts.length < totalProducts) {
      currentEnd -= this.getMerchandiseSlots();
    }
    
    const localeString = window.loadMoreStrings?.showingCount || 'Showing [start]-[end] of [total] products';
    this.countText.textContent = localeString
      .replace('[start]', '1')
      .replace('[end]', currentEnd)
      .replace('[total]', totalProducts);
  }

  getMerchandiseSlots() {
    const container = document.querySelector('.main-grid-wrapper');
    return container ? parseInt(container.dataset.merchandiseSlots || '0', 10) : 0;
  }

  checkForMorePages(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Check if there's a load-more button in the next page
    const nextLoadMore = doc.querySelector('load-more');
    return nextLoadMore !== null;
  }
}

customElements.define('load-more', LoadMore);
